import * as CL from '@design-system/component-library';
import { useContext, useRef } from 'react';
import { useLocation } from 'react-router';
import { JsonLd } from 'react-schemaorg';
import { pushToDataLayer } from '../../common/analytics';
import {
  mBpsMsg,
  orderMsg,
  recommendationUsersDevicesMsg,
  standalone5GMsg,
  t,
  uploadSpeedMsg,
} from '../../common/i18n';
import { AnalyticsListNames } from '../../common/utils/analyticsUtils';
import { SiteContext } from '../../public/site/SiteContext';
import { Grid } from '../Grid/Grid';
import { getViewItemListEventForPlaceOrder } from '../PlaceOrder/utils/placeOrderAnalyticsUtils';
import {
  itsenainen5GVerkkoContent,
  mobiiliturvaContent,
  reissaajaContent,
  reissunetti1GtContent,
  reissunetti2GtContent,
} from './HorizontalSubscriptionsContents';
import { SubscriptionCard } from './SubscriptionCard';
import { MobileCarouselWrapper, getItemList } from './SubscriptionUtils';
import type { HorizontalMobileBroadbandSubscriptionsProps } from './HorizontalMobileBroadbandSubscriptions';
import type { SubCardWithAnalyticsV4Props } from './subscriptionCardsAnalyticsUtils';
import type { SubProps, SubscriptionCardWithModel } from './types';
import type { ItemList } from 'schema-dts';

export const HorizontalMBBYritysdataSubscriptions = ({
  subscriptionCards,
  wide,
  compact = false,
  isChangeOffer = false,
  onOrder,
  getCard,
  buttonText,
  showDescriptions = false,
  hasQuantity = true,
  hideGroupHeaders = false,
  eventOrigin,
}: HorizontalMobileBroadbandSubscriptionsProps) => {
  const { pathname } = useLocation();
  const { siteBaseUrl } = useContext(SiteContext);

  const hasRunOnceRef = useRef(false);
  const analyticsListName = `${eventOrigin} ${AnalyticsListNames.MBB_YRITYSDATA_LIST_NAME}`;

  const sharedProps = {
    isChangeOffer,
    onOrder,
    subHeader: 'Yritysliittymä',
    buttonText: buttonText ?? t.AQOL(orderMsg),
    buttonMakesOrder: true,
    analyticsListName,
    showFixedTermInfo: true,
    compact,
  };

  const Sub1000m = ({ analyticsIndex }: SubProps) => (
    <SubscriptionCard
      subscriptionCard={getCard('1000m')}
      {...sharedProps}
      analyticsIndex={analyticsIndex}
      badges={[<CL.Badge key="5g" color="5g" text="5G+" />]}
      header="Yritysdata 1000 M"
      description={t.QF0D('For super-fast business use')}
      infoItems={[
        {
          icon: 'group',
          text: t.SDTV('All internet users and devices'),
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '100')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '110'),
        },
      ]}
      hasQuantity={!isChangeOffer && hasQuantity}
      additionalFeatures={[
        {
          icon: 'check',
          text: `Reissunetti 2${t.C4YQ('GB')}`,
          popoverContent: reissunetti2GtContent(),
        },
        {
          icon: 'check',
          text: t.ELB0(standalone5GMsg),
          popoverContent: itsenainen5GVerkkoContent(),
        },
        {
          icon: 'check',
          text: t.EH0U('Elisa Mobiiliturva for companies'),
          popoverContent: mobiiliturvaContent(),
        },
      ]}
    />
  );

  const Sub600m = ({ analyticsIndex }: SubProps) => (
    <SubscriptionCard
      subscriptionCard={getCard('600m')}
      {...sharedProps}
      analyticsIndex={analyticsIndex}
      badges={[<CL.Badge key="5g" color="5g" text="5G+" />]}
      header="Yritysdata 600 M"
      description={t.NXK9('For fast business use')}
      infoItems={[
        {
          icon: 'group',
          text: t.SDTL(recommendationUsersDevicesMsg, '4+', '15+'),
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '75')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '90'),
        },
      ]}
      hasQuantity={!isChangeOffer && hasQuantity}
      additionalFeatures={[
        {
          icon: 'check',
          text: `Reissunetti 1-1,5 ${t.C4YQ('GB')}`,
          popoverContent: reissunetti1GtContent(),
        },
        {
          icon: 'check',
          text: t.ELB0(standalone5GMsg),
          popoverContent: itsenainen5GVerkkoContent(),
        },
        {
          icon: 'check',
          text: t.EH0U('Elisa Mobiiliturva for companies'),
          popoverContent: mobiiliturvaContent(),
        },
      ]}
    />
  );

  const Sub300m = ({ analyticsIndex }: SubProps) => (
    <SubscriptionCard
      subscriptionCard={getCard('300m')}
      {...sharedProps}
      analyticsIndex={analyticsIndex}
      badges={[<CL.Badge key="5g" color="5g" text="5G+" />]}
      header="Yritysdata 300 M"
      description={t.EX8C('For active internet use')}
      infoItems={[
        {
          icon: 'group',
          text: t.SDTL(recommendationUsersDevicesMsg, '3-4', '10-15'),
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '50')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '80'),
        },
      ]}
      hasQuantity={!isChangeOffer && hasQuantity}
      additionalFeatures={[
        {
          icon: 'check',
          text: `Reissunetti 1-1,5 ${t.C4YQ('GB')}`,
          popoverContent: reissunetti1GtContent(),
        },
        {
          icon: 'check',
          text: t.ELB0(standalone5GMsg),
          popoverContent: itsenainen5GVerkkoContent(),
        },
      ]}
    />
  );

  const Sub100m = ({ analyticsIndex }: SubProps) => (
    <SubscriptionCard
      subscriptionCard={getCard('100m')}
      {...sharedProps}
      analyticsIndex={analyticsIndex}
      badges={[<CL.Badge key="5g" color="5g" text="5G+" />]}
      header="Yritysdata 100 M"
      description={t.SDTM('Smooth everyday use')}
      infoItems={[
        {
          icon: 'group',
          text: t.SDTL(recommendationUsersDevicesMsg, '1-2', '1-4'),
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '50')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '65'),
        },
      ]}
      hasQuantity={!isChangeOffer && hasQuantity}
      additionalFeatures={[
        {
          icon: 'check',
          text: 'Reissaaja',
          popoverContent: reissaajaContent(),
        },
        {
          icon: 'check',
          text: t.ELB0(standalone5GMsg),
          popoverContent: itsenainen5GVerkkoContent(),
        },
      ]}
    />
  );

  const Sub50m = ({ analyticsIndex }: SubProps) => (
    <SubscriptionCard
      subscriptionCard={getCard('50m')}
      {...sharedProps}
      analyticsIndex={analyticsIndex}
      badges={[]}
      header="Yritysdata 50 M"
      description={t.KVG8('Video calls, cloud services use and light remote working')}
      infoItems={[
        {
          icon: 'group',
          text: t.SDTL(recommendationUsersDevicesMsg, '1-2', '1-4'),
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '25')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '50'),
        },
      ]}
      hasQuantity={!isChangeOffer && hasQuantity}
      additionalFeatures={[
        {
          icon: 'check',
          text: 'Reissaaja',
          popoverContent: reissaajaContent(),
        },
      ]}
    />
  );

  const Sub10m = ({ analyticsIndex }: SubProps) => (
    <SubscriptionCard
      subscriptionCard={getCard('10m')}
      {...sharedProps}
      analyticsIndex={analyticsIndex}
      badges={[]}
      header="Yritysdata 10 M"
      description={t.KVG9('Light internet use, reading emails, sending messages')}
      infoItems={[
        {
          icon: 'group',
          text: t.SDTL(recommendationUsersDevicesMsg, '1-2', '1-4'),
        },
        {
          icon: 'speed',
          text: `${t.CQQ7(uploadSpeedMsg)} ${t.IJNS(mBpsMsg, '5')}`,
        },
        {
          icon: 'globe',
          text: t.IJ5P('EU data {} GB/month', '30'),
        },
      ]}
      hasQuantity={!isChangeOffer && hasQuantity}
      additionalFeatures={[
        {
          icon: 'check',
          text: 'Reissaaja',
          popoverContent: reissaajaContent(),
        },
      ]}
    />
  );

  let aIndex = 0;
  const availableCardsList = subscriptionCards.map(subscriptionCard => subscriptionCard.cardName || '');
  const yritysDataCards = [
    { name: '1000m', component: Sub1000m },
    { name: '600m', component: Sub600m },
    { name: '300m', component: Sub300m },
    { name: '100m', component: Sub100m },
    { name: '50m', component: Sub50m },
    { name: '10m', component: Sub10m },
  ].filter(card => availableCardsList.includes(card.name));

  const filteredSubscriptionCards = (subscriptionCards as SubscriptionCardWithModel[]).filter(subCard =>
    yritysDataCards.some(card => card.name === subCard.cardName)
  );

  if (!hasRunOnceRef.current && filteredSubscriptionCards.every(subCard => subCard.onlineModel?.onlineModelName)) {
    const analyticsEvent = getViewItemListEventForPlaceOrder(
      filteredSubscriptionCards as SubCardWithAnalyticsV4Props[],
      analyticsListName
    );
    pushToDataLayer(analyticsEvent);
    hasRunOnceRef.current = true;
  }

  const content = (
    <div className="of-subscriptions-main-container">
      {hideGroupHeaders ? (
        <div className="of-subscriptions-main-container-header-spacer" />
      ) : (
        <h2 className="of-subscriptions-main-container-carousel-title">Yritysdata</h2>
      )}
      {showDescriptions && (
        <p className="ds-text--l">{t.DBD3('Includes the subscription only. Terminal devices are sold separately.')}</p>
      )}
      <MobileCarouselWrapper>
        {yritysDataCards.map(card => {
          const Component = card.component;
          return <Component key={card.name} analyticsIndex={aIndex++} />;
        })}
      </MobileCarouselWrapper>
      <JsonLd<ItemList> item={getItemList(subscriptionCards as SubscriptionCardWithModel[], siteBaseUrl, pathname)} />
    </div>
  );

  if (compact) {
    return content;
  }

  return <Grid colWidthL={wide ? 12 : 10}>{content}</Grid>;
};
